import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom'; // Import useParams
import { auth, db, updateUserDisplayName } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore'; // Import Firestore query functions
import { sendPasswordResetEmail } from 'firebase/auth'; // Import sendPasswordResetEmail
import { useAuth } from '../contexts/AuthContext'; // Import useAuth
import { getFunctions, httpsCallable } from 'firebase/functions'; // Import Firebase Functions SDK
import { useTranslation } from 'react-i18next'; // Import useTranslation
import './UserSettingsPage.css'; // Import CSS file

const UserSettingsPage = () => {
  const { t } = useTranslation(); // Add translation hook
  const { currentUser, // Get currentUser from useAuth
          // We will fetch claims specifically for this page or assume it might come from useAuth if extended
        } = useAuth(); 
  const { hotelId } = useParams(); // Get hotelId from URL parameters

  // State for current user's profile
  const [currentDisplayName, setCurrentDisplayName] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // State for password reset
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState(null);

  // State for User Admin section
  const [userClaims, setUserClaims] = useState(null);
  const [hotelUsers, setHotelUsers] = useState([]);
  const [adminSectionLoading, setAdminSectionLoading] = useState(false);
  const [adminSectionError, setAdminSectionError] = useState(null);
  const [refetchHotelUsersTrigger, setRefetchHotelUsersTrigger] = useState(0); // For refreshing user list
  const [currentHotelDisplayName, setCurrentHotelDisplayName] = useState(null); // New state for hotel display name

  // Create New User form state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState(null);
  const [createUserSuccessMessage, setCreateUserSuccessMessage] = useState(null);

  // New state for per-user update loading/error
  const [updatingUserClaims, setUpdatingUserClaims] = useState({});
  const [updateUserClaimsError, setUpdateUserClaimsError] = useState({});

  // State for display name editing
  const [editingDisplayNames, setEditingDisplayNames] = useState({});
  const [updatingDisplayNames, setUpdatingDisplayNames] = useState({});
  const [displayNameErrors, setDisplayNameErrors] = useState({});

  // State for hotel settings
  const [hotelSettings, setHotelSettings] = useState(null);
  const [loadingHotelSettings, setLoadingHotelSettings] = useState(false);
  const [hotelSettingsError, setHotelSettingsError] = useState(null);
  const [updatingHotelSettings, setUpdatingHotelSettings] = useState(false);
  const [productionMode, setProductionMode] = useState(false);
  const [published, setPublished] = useState(false);

  // State for shipping address configuration
  const [shippingConfig, setShippingConfig] = useState({
    senderName: '',
    senderAddress: {
      line1: '',
      line2: '',
      line3: '',
      postalCode: ''
    },
    senderPhone: '',
    senderEmail: '',
    sagawa: {
      shukaIraiTimeCode: ''
    }
  });
  const [savingShippingConfig, setSavingShippingConfig] = useState(false);
  const [shippingConfigError, setShippingConfigError] = useState(null);
  const [shippingConfigSuccess, setShippingConfigSuccess] = useState(null);

  // Effect for fetching current user's profile and claims
  useEffect(() => {
    if (currentUser) {
      setLoadingProfile(true);
      setProfileError(null);
      const fetchUserData = async () => {
        try {
          // Fetch display name from user's document
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const firestoreUserData = userDocSnap.data(); // Renamed to avoid conflict
            // Fetch user claims from token
            const idTokenResult = await currentUser.getIdTokenResult(true); // Force refresh of token
            let fetchedClaims = idTokenResult.claims;

            // Add claimsVersion from Firestore document to the claims object for display
            if (firestoreUserData.claimsVersion !== undefined) {
              fetchedClaims = { ...fetchedClaims, claimsVersion: firestoreUserData.claimsVersion };
            }
             // Set display name from Firestore doc data
            setCurrentDisplayName(firestoreUserData.displayName || '');
            setNewDisplayName(firestoreUserData.displayName || '');
            setUserClaims(fetchedClaims);
          } else {
            console.log("No such user document for profile!");
            setCurrentDisplayName('');
            setNewDisplayName('');
            setUserClaims(null);
          }
        } catch (err) {
          console.error("Error fetching user data or claims:", err);
          setProfileError(t('userSettings.errors.profile'));
          setUserClaims(null); // Clear claims on error
        }
        setLoadingProfile(false);
      };
      fetchUserData();
    } else {
      // No current user
      setLoadingProfile(false);
      setProfileError(t('userSettings.errors.login'));
      setCurrentDisplayName('');
      setNewDisplayName('');
      setUserClaims(null);
    }
  }, [currentUser]);

  // Effect for fetching hotel display name AND users list via Firebase Function
  useEffect(() => {
    const fetchAdminSectionData = async () => {
      if (!hotelId) {
        setCurrentHotelDisplayName(null);
        setHotelUsers([]);
        setAdminSectionLoading(false);
        setAdminSectionError(null); 
        return;
      }

      setAdminSectionLoading(true);
      setAdminSectionError(null);
      setCurrentHotelDisplayName(null); 
      setHotelUsers([]); 

      try {
        const hotelDocRef = doc(db, "hotels", hotelId);
        const hotelDocSnap = await getDoc(hotelDocRef);
        if (hotelDocSnap.exists()) {
          setCurrentHotelDisplayName(hotelDocSnap.data().displayName || hotelId);
        } else {
          console.warn(`Hotel document for ${hotelId} not found.`);
          setCurrentHotelDisplayName(hotelId); 
        }

        if (userClaims?.admin) {
          const functions = getFunctions();
          const getHotelUsersListCallable = httpsCallable(functions, 'getHotelUsersList');
          const result = await getHotelUsersListCallable({ targetHotelId: hotelId });
          
          if (result.data.success) {
            // Augment user data with local editable claims state
            const usersWithEditableClaims = result.data.users.map(user => ({
              ...user,
              localAdminClaim: user.claims?.admin || false,
              localStaffClaim: user.claims?.staff || false,
            }));
            setHotelUsers(usersWithEditableClaims);
          } else {
            let message = t('userSettings.errors.createUserFailed');
            if (result.data && typeof result.data === 'object' && result.data.hasOwnProperty('message') && typeof result.data.message === 'string') {
                 message = result.data.message;
            }
            setAdminSectionError(message);
            setHotelUsers([]);
          }
        } else {
          setHotelUsers([]); 
          if (userClaims !== null) { 
             setAdminSectionError(t('userSettings.errors.noPermission'));
          }
        }
      } catch (error) {
        console.error("Error in fetchAdminSectionData (calling function or fetching hotel):", error);
        let message = t('userSettings.errors.adminDataFailed');
        if (error && typeof error === 'object' && error.hasOwnProperty('message') && typeof error.message === 'string') {
            message = error.message;
        }
        setAdminSectionError(message);
        setHotelUsers([]);
        setCurrentHotelDisplayName(hotelId); 
      } finally {
        setAdminSectionLoading(false);
      }
    };

    // Fetch hotel settings
    const fetchHotelSettings = async () => {
      if (!hotelId || !userClaims?.admin) {
        setHotelSettings(null);
        setLoadingHotelSettings(false);
        setHotelSettingsError(null);
        return;
      }

      setLoadingHotelSettings(true);
      setHotelSettingsError(null);

      try {
        const hotelDocRef = doc(db, "hotels", hotelId);
        const hotelDocSnap = await getDoc(hotelDocRef);

        if (hotelDocSnap.exists()) {
          const hotelData = hotelDocSnap.data();
          setHotelSettings(hotelData.settings || { emailNotifications: false });
          setProductionMode(hotelData.productionMode === true);
          setPublished(hotelData.published === true);

          // Load shipping configuration
          const shipping = hotelData.shipping || {};
          setShippingConfig({
            senderName: shipping.senderName || hotelData.contactName || hotelData.displayName || '',
            senderAddress: {
              line1: shipping.senderAddress?.line1 || hotelData.address?.line1 || hotelData.addressLine1 || '',
              line2: shipping.senderAddress?.line2 || hotelData.address?.line2 || hotelData.addressLine2 || '',
              line3: shipping.senderAddress?.line3 || hotelData.address?.line3 || hotelData.addressLine3 || '',
              postalCode: shipping.senderAddress?.postalCode || hotelData.address?.postalCode || hotelData.postalCode || ''
            },
            senderPhone: shipping.senderPhone || hotelData.phone || '',
            senderEmail: shipping.senderEmail || hotelData.email || '',
            sagawa: {
              shukaIraiTimeCode: shipping.sagawa?.shukaIraiTimeCode || ''
            }
          });
        } else {
          setHotelSettingsError(t('userSettings.errors.hotelSettingsFailed'));
          setHotelSettings(null);
        }
      } catch (error) {
        console.error("Error fetching hotel settings:", error);
        setHotelSettingsError(t('userSettings.errors.hotelSettingsFailed'));
        setHotelSettings(null);
      } finally {
        setLoadingHotelSettings(false);
      }
    };

    if (userClaims !== null || !currentUser) {
        fetchAdminSectionData();
        fetchHotelSettings();
    }

  }, [userClaims, hotelId, refetchHotelUsersTrigger, currentUser]);

  const handleDisplayNameUpdate = async (e) => {
    e.preventDefault();
    setProfileError(null);
    setSuccessMessage(null);
    if (!currentUser) {
      setProfileError(t('userSettings.errors.noUser'));
      return;
    }
    if (newDisplayName.trim() === currentDisplayName) {
      setProfileError(t('userSettings.errors.sameDisplayName'));
      return;
    }
    if (!newDisplayName.trim()) {
        setProfileError(t('userSettings.errors.emptyDisplayName'));
        return;
    }
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, { displayName: newDisplayName.trim() });
      // Also update Firebase Auth profile if desired, though not strictly necessary for this app's display
      // await updateProfile(auth.currentUser, { displayName: newDisplayName.trim() });
      setCurrentDisplayName(newDisplayName.trim());
      setSuccessMessage(t('userSettings.success.displayNameUpdated'));
    } catch (err) {
      console.error("Error updating display name:", err);
      setProfileError(t('userSettings.errors.updateDisplayName') + " " + err.message);
    }
  };

  const handlePasswordReset = async () => {
    if (!currentUser || !currentUser.email) {
      setPasswordResetError(t('userSettings.errors.noUser'));
      return;
    }

    setSendingPasswordReset(true);
    setPasswordResetError(null);
    setPasswordResetSuccess(false);

    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      setPasswordResetSuccess(true);
      console.log('Password reset email sent to:', currentUser.email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      let errorMessage = t('userSettings.errors.passwordResetFailed');
      
      // Handle specific Firebase Auth error codes
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = t('userSettings.errors.userNotFound');
          break;
        case 'auth/too-many-requests':
          errorMessage = t('userSettings.errors.tooManyRequests');
          break;
        case 'auth/network-request-failed':
          errorMessage = t('userSettings.errors.networkError');
          break;
        default:
          errorMessage = t('userSettings.errors.passwordResetFailed');
      }
      
      setPasswordResetError(errorMessage);
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateUserError(null);
    setCreateUserSuccessMessage(null);
    if (!newUserEmail || !newUserPassword) {
      setCreateUserError(t('userSettings.errors.emailRequired'));
      return;
    }
    if (newUserPassword.length < 6) {
      setCreateUserError(t('userSettings.errors.passwordLength'));
      return;
    }
    if (!hotelId) {
        setCreateUserError(t('userSettings.errors.missingHotelId'));
        return;
    }
    setCreatingUser(true);
    try {
      const functions = getFunctions();
      const createNewHotelUserCallable = httpsCallable(functions, 'createNewHotelUser');
      const result = await createNewHotelUserCallable({ 
        email: newUserEmail, 
        password: newUserPassword, 
        targetHotelId: hotelId 
      });
      if (result.data.success) {
        setCreateUserSuccessMessage(result.data.message || t('userSettings.success.userCreated'));
        setNewUserEmail('');
        setNewUserPassword('');
        setRefetchHotelUsersTrigger(prev => prev + 1);
      } else {
        setCreateUserError(result.data.message || t('userSettings.errors.createUserFailed'));
      }
    } catch (error) {
      console.error("Error calling createNewHotelUser function:", error);
      let errorMessage = t('userSettings.errors.unexpectedError');
      if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }
      setCreateUserError(errorMessage);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleClaimChange = (userId, claimType, value) => {
    setHotelUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === userId 
          ? { ...user, [claimType === 'admin' ? 'localAdminClaim' : 'localStaffClaim']: value }
          : user
      )
    );
  };

  const handleSaveChangesForUser = async (userId) => {
    const userToUpdate = hotelUsers.find(user => user.id === userId);
    if (!userToUpdate) return;

    setUpdatingUserClaims(prev => ({ ...prev, [userId]: true }));
    setUpdateUserClaimsError(prev => ({ ...prev, [userId]: null }));

    const newClaims = {
      admin: userToUpdate.localAdminClaim,
      staff: userToUpdate.localStaffClaim,
    };

    try {
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, { claims: newClaims });
      // Optionally, you can update the main `user.claims` in the local state too 
      // or rely on refetch for perfect sync, but for responsiveness:
      setHotelUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userId ? { ...u, claims: newClaims } : u
        )
      );
      // Consider a success message, perhaps per row or a general one.
    } catch (error) {
      console.error(`Error updating claims for user ${userId}:`, error);
      let message = t('userSettings.errors.updateClaimsFailed');
      if (error && typeof error === 'object' && error.hasOwnProperty('message') && typeof error.message === 'string') {
        message = error.message;
      }
      setUpdateUserClaimsError(prev => ({ ...prev, [userId]: message }));
      // Revert local changes on error if desired, or prompt user
      // For now, local changes persist, user sees error and can retry save
    } finally {
      setUpdatingUserClaims(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleEmailNotificationToggle = async (enabled) => {
    if (!userClaims?.admin || !hotelId) {
      setHotelSettingsError(t('userSettings.errors.adminOnly'));
      return;
    }

    setUpdatingHotelSettings(true);
    setHotelSettingsError(null);

    try {
      const hotelDocRef = doc(db, "hotels", hotelId);
      await updateDoc(hotelDocRef, {
        "settings.emailNotifications": enabled,
        "settings.updatedAt": new Date(),
      });

      // Update local state
      setHotelSettings(prev => ({
        ...prev,
        emailNotifications: enabled,
        updatedAt: new Date(),
      }));

      console.log(`Email notifications ${enabled ? 'enabled' : 'disabled'} for hotel: ${hotelId}`);
    } catch (error) {
      console.error("Error updating email notification setting:", error);
      setHotelSettingsError(t('userSettings.errors.hotelSettingsFailed'));
    } finally {
      setUpdatingHotelSettings(false);
    }
  };

  const handleProductionModeToggle = async (enabled) => {
    if (!userClaims?.admin || !hotelId) {
      setHotelSettingsError(t('userSettings.errors.adminOnly'));
      return;
    }

    setUpdatingHotelSettings(true);
    setHotelSettingsError(null);

    try {
      const hotelDocRef = doc(db, "hotels", hotelId);
      await updateDoc(hotelDocRef, {
        productionMode: enabled,
        updatedAt: new Date(),
      });

      // Update local state
      setProductionMode(enabled);

      console.log(`Production mode ${enabled ? 'enabled' : 'disabled'} for hotel: ${hotelId}`);
    } catch (error) {
      console.error("Error updating production mode setting:", error);
      setHotelSettingsError(t('userSettings.errors.hotelSettingsFailed'));
    } finally {
      setUpdatingHotelSettings(false);
    }
  };

  const handlePublishedToggle = async (enabled) => {
    if (!userClaims?.admin || !hotelId) {
      setHotelSettingsError(t('userSettings.errors.adminOnly'));
      return;
    }

    setUpdatingHotelSettings(true);
    setHotelSettingsError(null);

    try {
      const hotelDocRef = doc(db, "hotels", hotelId);
      await updateDoc(hotelDocRef, {
        published: enabled,
        updatedAt: new Date(),
      });
      setPublished(enabled);
    } catch (error) {
      console.error("Error updating published setting:", error);
      setHotelSettingsError(t('userSettings.errors.hotelSettingsFailed'));
    } finally {
      setUpdatingHotelSettings(false);
    }
  };

  const handleSaveShippingConfig = async (e) => {
    e.preventDefault();
    if (!userClaims?.admin || !hotelId) {
      setShippingConfigError(t('userSettings.errors.adminOnly'));
      return;
    }

    setSavingShippingConfig(true);
    setShippingConfigError(null);
    setShippingConfigSuccess(null);

    try {
      const hotelDocRef = doc(db, "hotels", hotelId);
      await updateDoc(hotelDocRef, {
        "shipping.senderName": shippingConfig.senderName,
        "shipping.senderAddress.line1": shippingConfig.senderAddress.line1,
        "shipping.senderAddress.line2": shippingConfig.senderAddress.line2,
        "shipping.senderAddress.line3": shippingConfig.senderAddress.line3,
        "shipping.senderAddress.postalCode": shippingConfig.senderAddress.postalCode,
        "shipping.senderPhone": shippingConfig.senderPhone,
        "shipping.senderEmail": shippingConfig.senderEmail,
        "shipping.sagawa.shukaIraiTimeCode": shippingConfig.sagawa.shukaIraiTimeCode,
        "shipping.updatedAt": new Date(),
      });

      setShippingConfigSuccess(t('userSettings.success.shippingConfigUpdated'));
      console.log(`Shipping configuration updated for hotel: ${hotelId}`);
    } catch (error) {
      console.error("Error updating shipping configuration:", error);
      setShippingConfigError(t('userSettings.errors.shippingConfigFailed'));
    } finally {
      setSavingShippingConfig(false);
    }
  };

  // Handler for starting display name editing
  const handleStartEditDisplayName = (userId, currentDisplayName) => {
    setEditingDisplayNames(prev => ({
      ...prev,
      [userId]: currentDisplayName || ''
    }));
    setDisplayNameErrors(prev => ({
      ...prev,
      [userId]: null
    }));
  };

  // Handler for canceling display name editing
  const handleCancelEditDisplayName = (userId) => {
    setEditingDisplayNames(prev => {
      const updated = { ...prev };
      delete updated[userId];
      return updated;
    });
    setDisplayNameErrors(prev => {
      const updated = { ...prev };
      delete updated[userId];
      return updated;
    });
  };

  // Handler for display name input change
  const handleDisplayNameChange = (userId, value) => {
    setEditingDisplayNames(prev => ({
      ...prev,
      [userId]: value
    }));
  };

  // Handler for saving display name
  const handleSaveDisplayName = async (userId) => {
    const newDisplayName = editingDisplayNames[userId];
    
    if (!newDisplayName || newDisplayName.trim() === '') {
      setDisplayNameErrors(prev => ({
        ...prev,
        [userId]: 'Display name cannot be empty'
      }));
      return;
    }

    setUpdatingDisplayNames(prev => ({
      ...prev,
      [userId]: true
    }));
    setDisplayNameErrors(prev => ({
      ...prev,
      [userId]: null
    }));

    try {
      const result = await updateUserDisplayName({
        targetUserId: userId,
        newDisplayName: newDisplayName.trim(),
        hotelId: hotelId
      });

      if (result.data && result.data.success) {
        // Update the local state to reflect the change
        setHotelUsers(prev => prev.map(user => 
          user.id === userId 
            ? { ...user, displayName: newDisplayName.trim() }
            : user
        ));
        
        // Clear editing state
        handleCancelEditDisplayName(userId);
        
        console.log('Display name updated successfully:', result.data.message);
      } else {
        throw new Error(result.data?.message || 'Failed to update display name');
      }
    } catch (error) {
      console.error('Error updating display name:', error);
      setDisplayNameErrors(prev => ({
        ...prev,
        [userId]: error.message || 'Failed to update display name'
      }));
    } finally {
      setUpdatingDisplayNames(prev => ({
        ...prev,
        [userId]: false
      }));
    }
  };

  if (loadingProfile && !currentUser) { // Initial load check before currentUser is definitely known
    return (
      <div className="settings-container">
        <div className="loading-spinner">
          {t('userSettings.loading.user')}
        </div>
      </div>
    );
  }

  if (!currentUser) { 
    return (
      <div className="settings-container">
        <div className="error-message">
          Error: {profileError || t('userSettings.errors.login')}
        </div>
      </div>
    );
  }
  
  // At this point, currentUser exists. Profile might still be loading or have an error.

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1 className="settings-title">{t('userSettings.title')}</h1>
      </div>

      {/* Profile Section */}
      <div className="settings-card">
        <div className="card-header">
          <h2 className="card-title">{t('userSettings.profile.title')}</h2>
        </div>
        <div className="card-content">
          {loadingProfile && <div className="loading-text">{t('userSettings.loading.profile')}</div>}
          {profileError && <div className="error-message">{profileError}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}
          
          {!loadingProfile && currentUser && (
            <>
              <div className="profile-info">
                <div className="info-row">
                  <span className="info-label">{t('userSettings.profile.email')}:</span>
                  <span className="info-value">{currentUser.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('userSettings.profile.displayName')}:</span>
                  <span className="info-value">{currentDisplayName || t('userSettings.profile.notSet')}</span>
                </div>
              </div>
              
              {userClaims && (
                <div className="permissions-card">
                  <h3 className="permissions-title">{t('userSettings.profile.permissions')}</h3>
                  <div className="permissions-grid">
                    <div className="permission-item">
                      <span className="permission-label">{t('userSettings.profile.admin')}:</span>
                      <span className={`permission-badge ${userClaims.admin ? 'active' : 'inactive'}`}>
                        {userClaims.admin ? t('userSettings.profile.yes') : t('userSettings.profile.no')}
                      </span>
                    </div>
                    <div className="permission-item">
                      <span className="permission-label">{t('userSettings.profile.staff')}:</span>
                      <span className={`permission-badge ${userClaims.staff ? 'active' : 'inactive'}`}>
                        {userClaims.staff ? t('userSettings.profile.yes') : t('userSettings.profile.no')}
                      </span>
                    </div>
                    <div className="permission-item full-width">
                      <span className="permission-label">{t('userSettings.profile.accessibleHotels')}:</span>
                      <span className="permission-value">
                        {userClaims.accessibleHotels && userClaims.accessibleHotels.length > 0 
                          ? userClaims.accessibleHotels.join(', ') 
                          : t('userSettings.profile.none')}
                      </span>
                    </div>
                    <div className="permission-item">
                      <span className="permission-label">{t('userSettings.profile.claimsVersion')}:</span>
                      <span className="permission-value">{userClaims.claimsVersion || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="update-form">
                <h3 className="form-title">{t('userSettings.profile.updateDisplayName')}</h3>
                <form onSubmit={handleDisplayNameUpdate} className="display-name-form">
                  <div className="form-group">
                    <input
                      type="text"
                      id="displayName"
                      value={newDisplayName}
                      onChange={(e) => setNewDisplayName(e.target.value)}
                      className="form-input"
                      placeholder={t('userSettings.profile.displayName')}
                      disabled={loadingProfile}
                    />
                    <button type="submit" disabled={loadingProfile} className="btn btn-primary">
                      {t('userSettings.profile.saveDisplayName')}
                    </button>
                  </div>
                </form>
              </div>

              <div className="password-reset-form">
                <h3 className="form-title">{t('userSettings.profile.resetPassword')}</h3>
                <div className="password-reset-section">
                  <p className="reset-description">
                    {t('userSettings.profile.resetPasswordDescription', { email: currentUser.email })}
                  </p>
                  
                  {passwordResetError && (
                    <div className="error-message">{passwordResetError}</div>
                  )}
                  
                  {passwordResetSuccess && (
                    <div className="success-message">
                      {t('userSettings.success.passwordResetEmailSent', { email: currentUser.email })}
                    </div>
                  )}
                  
                  <button 
                    onClick={handlePasswordReset}
                    disabled={sendingPasswordReset || passwordResetSuccess}
                    className="btn btn-secondary reset-password-btn"
                  >
                    {sendingPasswordReset 
                      ? t('userSettings.profile.sendingPasswordReset') 
                      : t('userSettings.profile.sendPasswordReset')
                    }
                  </button>
                  
                  {passwordResetSuccess && (
                    <p className="reset-instructions">
                      {t('userSettings.profile.checkEmailInstructions')}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* User Administration Section */}
      {userClaims?.admin && hotelId && (
        <div className="settings-card">
          <div className="card-header">
            <h2 className="card-title">{t('userSettings.userAdmin.title')}: {currentHotelDisplayName || hotelId}</h2>
          </div>
          <div className="card-content">
            {/* Create New User Form */}
            <div className="create-user-section">
              <h3 className="section-title">{t('userSettings.userAdmin.createUser')} {currentHotelDisplayName || hotelId}</h3>
              {createUserError && <div className="error-message">{createUserError}</div>}
              {createUserSuccessMessage && <div className="success-message">{createUserSuccessMessage}</div>}
              <form onSubmit={handleCreateUser} className="create-user-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="newUserEmail" className="form-label">{t('userSettings.userAdmin.email')}</label>
                    <input 
                      type="email" 
                      id="newUserEmail" 
                      value={newUserEmail} 
                      onChange={(e) => setNewUserEmail(e.target.value)} 
                      className="form-input"
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="newUserPassword" className="form-label">{t('userSettings.userAdmin.password')}</label>
                    <input 
                      type="password" 
                      id="newUserPassword" 
                      value={newUserPassword} 
                      onChange={(e) => setNewUserPassword(e.target.value)} 
                      className="form-input"
                      required 
                      minLength={6}
                    />
                  </div>
                </div>
                <button type="submit" disabled={creatingUser} className="btn btn-primary">
                  {creatingUser ? t('userSettings.userAdmin.creatingButton') : t('userSettings.userAdmin.createButton')}
                </button>
              </form>
            </div>
            
            {/* Users Table */}
            <div className="users-section">
              {adminSectionLoading && <div className="loading-text">{t('userSettings.loading.users')}</div>}
              {adminSectionError && <div className="error-message">{adminSectionError}</div>}
              {!adminSectionLoading && !adminSectionError && hotelUsers.length === 0 && userClaims?.admin && (
                <div className="no-data-message">{t('userSettings.userAdmin.noUsers')}</div>
              )}
              {!adminSectionLoading && !adminSectionError && hotelUsers.length > 0 && userClaims?.admin && (
                <div className="table-container">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>{t('userSettings.userAdmin.table.displayName')}</th>
                        <th>{t('userSettings.userAdmin.table.email')}</th>
                        <th>{t('userSettings.userAdmin.table.admin')}</th>
                        <th>{t('userSettings.userAdmin.table.staff')}</th>
                        <th>{t('userSettings.userAdmin.table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hotelUsers.map(user => (
                        <tr key={user.id}>
                          <td>
                            {editingDisplayNames[user.id] !== undefined ? (
                              // Editing mode
                              <div className="display-name-edit">
                                <input
                                  type="text"
                                  value={editingDisplayNames[user.id]}
                                  onChange={(e) => handleDisplayNameChange(user.id, e.target.value)}
                                  className="form-input form-input-sm"
                                  placeholder="Enter display name"
                                  disabled={updatingDisplayNames[user.id]}
                                />
                                <div className="display-name-actions">
                                  <button
                                    onClick={() => handleSaveDisplayName(user.id)}
                                    disabled={updatingDisplayNames[user.id]}
                                    className="btn btn-xs btn-primary"
                                  >
                                    {updatingDisplayNames[user.id] ? '...' : '✓'}
                                  </button>
                                  <button
                                    onClick={() => handleCancelEditDisplayName(user.id)}
                                    disabled={updatingDisplayNames[user.id]}
                                    className="btn btn-xs btn-secondary"
                                  >
                                    ✕
                                  </button>
                                </div>
                                {displayNameErrors[user.id] && (
                                  <div className="error-text-sm">{displayNameErrors[user.id]}</div>
                                )}
                              </div>
                            ) : (
                              // Display mode
                              <div className="display-name-view">
                                <span className="display-name-text">
                                  {user.displayName || user.email || t('userSettings.profile.notSet')}
                                </span>
                                {!user.claims?.admin && (
                                  <button
                                    onClick={() => handleStartEditDisplayName(user.id, user.displayName)}
                                    className="btn btn-xs btn-outline edit-display-name-btn"
                                    title="Edit display name"
                                  >
                                    ✏️
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <input 
                              type="checkbox" 
                              checked={user.localAdminClaim}
                              onChange={(e) => handleClaimChange(user.id, 'admin', e.target.checked)}
                              disabled={updatingUserClaims[user.id] || user.id === currentUser.uid}
                              className="checkbox"
                            />
                          </td>
                          <td>
                            <input 
                              type="checkbox" 
                              checked={user.localStaffClaim} 
                              onChange={(e) => handleClaimChange(user.id, 'staff', e.target.checked)}
                              disabled={updatingUserClaims[user.id]}
                              className="checkbox"
                            />
                          </td>
                          <td>
                            <button 
                              onClick={() => handleSaveChangesForUser(user.id)} 
                              disabled={updatingUserClaims[user.id] || 
                                        (user.claims?.admin === user.localAdminClaim && user.claims?.staff === user.localStaffClaim)
                                       }
                              className="btn btn-sm btn-primary"
                            >
                              {updatingUserClaims[user.id] ? t('userSettings.userAdmin.table.saving') : t('userSettings.userAdmin.table.save')}
                            </button>
                            {updateUserClaimsError[user.id] && <div className="error-text">{updateUserClaimsError[user.id]}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hotel Settings Section */}
      {userClaims?.admin && hotelId && (
        <div className="settings-card">
          <div className="card-header">
            <h2 className="card-title">{t('userSettings.hotelSettings.title')} {currentHotelDisplayName || hotelId}</h2>
          </div>
          <div className="card-content">
            {loadingHotelSettings && <div className="loading-text">{t('userSettings.loading.hotelSettings')}</div>}
            {hotelSettingsError && <div className="error-message">{hotelSettingsError}</div>}

            {!loadingHotelSettings && hotelSettings && (
              <div className="hotel-settings">
                <div className="setting-section">
                  <h3 className="setting-title">{t('userSettings.hotelSettings.emailNotifications')}</h3>
                  <div className="setting-card">
                    <div className="setting-header">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={hotelSettings.emailNotifications || false}
                          onChange={(e) => handleEmailNotificationToggle(e.target.checked)}
                          disabled={updatingHotelSettings}
                          className="toggle-checkbox"
                        />
                        <span className="toggle-text">
                          {t('userSettings.hotelSettings.emailToggleLabel')}
                        </span>
                      </label>
                      {updatingHotelSettings && <span className="updating-text">{t('userSettings.hotelSettings.updating')}</span>}
                    </div>
                    <p className="setting-description">
                      {t('userSettings.hotelSettings.emailDescription')} <strong>{t('userSettings.hotelSettings.defaultDisabled')}</strong>
                    </p>
                    {hotelSettings.updatedAt && (
                      <p className="last-updated">
                        {t('userSettings.hotelSettings.lastUpdated')}: {new Date(hotelSettings.updatedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="setting-section">
                  <h3 className="setting-title">Shipping Production Mode</h3>
                  <div className="setting-card">
                    <div className="setting-header">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={productionMode}
                          onChange={(e) => handleProductionModeToggle(e.target.checked)}
                          disabled={updatingHotelSettings}
                          className="toggle-checkbox"
                        />
                        <span className="toggle-text">
                          Enable Production Mode
                        </span>
                      </label>
                      {updatingHotelSettings && <span className="updating-text">Updating...</span>}
                    </div>
                    <p className="setting-description">
                      When enabled, uses production Sagawa shipping credentials. When disabled, uses test credentials. <strong>Default: Test mode</strong>
                    </p>
                    <div className={`mode-indicator ${productionMode ? 'production' : 'test'}`}>
                      {productionMode ? (
                        <>
                          <strong>⚠️ Production Mode Active</strong>
                          <div>Live shipping labels will be generated using production credentials</div>
                        </>
                      ) : (
                        <>
                          <strong>🧪 Test Mode Active</strong>
                          <div>Test shipping labels will be generated using test credentials</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="setting-section">
                  <h3 className="setting-title">Public Listing</h3>
                  <div className="setting-card">
                    <div className="setting-header">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={published}
                          onChange={(e) => handlePublishedToggle(e.target.checked)}
                          disabled={updatingHotelSettings}
                          className="toggle-checkbox"
                        />
                        <span className="toggle-text">
                          List this hotel on quikbak.com
                        </span>
                      </label>
                      {updatingHotelSettings && <span className="updating-text">Updating...</span>}
                    </div>
                    <p className="setting-description">
                      When enabled, guests can find this hotel in the public hotel list on the landing page. When disabled, the hotel is hidden from the public site but still reachable by direct link for testing. <strong>Default: Hidden</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Shipping Configuration Section */}
      {userClaims?.admin && hotelId && (
        <div className="settings-card">
          <div className="card-header">
            <h2 className="card-title">{t('userSettings.shippingConfig.title')} {currentHotelDisplayName || hotelId}</h2>
          </div>
          <div className="card-content">
            {shippingConfigError && <div className="error-message">{shippingConfigError}</div>}
            {shippingConfigSuccess && <div className="success-message">{shippingConfigSuccess}</div>}

            <form onSubmit={handleSaveShippingConfig} className="shipping-config-form">
              <div className="form-section">
                <h3 className="section-title">{t('userSettings.shippingConfig.senderInfo')}</h3>

                <div className="form-group">
                  <label htmlFor="senderName" className="form-label">
                    {t('userSettings.shippingConfig.senderName')}
                  </label>
                  <input
                    type="text"
                    id="senderName"
                    value={shippingConfig.senderName}
                    onChange={(e) => setShippingConfig(prev => ({ ...prev, senderName: e.target.value }))}
                    className="form-input"
                    placeholder={t('userSettings.shippingConfig.senderNamePlaceholder')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="senderPhone" className="form-label">
                    {t('userSettings.shippingConfig.phone')}
                  </label>
                  <input
                    type="tel"
                    id="senderPhone"
                    value={shippingConfig.senderPhone}
                    onChange={(e) => setShippingConfig(prev => ({ ...prev, senderPhone: e.target.value }))}
                    className="form-input"
                    placeholder={t('userSettings.shippingConfig.phonePlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="senderEmail" className="form-label">
                    {t('userSettings.shippingConfig.email')}
                  </label>
                  <input
                    type="email"
                    id="senderEmail"
                    value={shippingConfig.senderEmail}
                    onChange={(e) => setShippingConfig(prev => ({ ...prev, senderEmail: e.target.value }))}
                    className="form-input"
                    placeholder={t('userSettings.shippingConfig.emailPlaceholder')}
                  />
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">{t('userSettings.shippingConfig.address')}</h3>

                <div className="form-group">
                  <label htmlFor="addressLine1" className="form-label">
                    {t('userSettings.shippingConfig.addressLine1')}
                  </label>
                  <input
                    type="text"
                    id="addressLine1"
                    value={shippingConfig.senderAddress.line1}
                    onChange={(e) => setShippingConfig(prev => ({
                      ...prev,
                      senderAddress: { ...prev.senderAddress, line1: e.target.value }
                    }))}
                    className="form-input"
                    placeholder={t('userSettings.shippingConfig.addressLine1Placeholder')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="addressLine2" className="form-label">
                    {t('userSettings.shippingConfig.addressLine2')}
                  </label>
                  <input
                    type="text"
                    id="addressLine2"
                    value={shippingConfig.senderAddress.line2}
                    onChange={(e) => setShippingConfig(prev => ({
                      ...prev,
                      senderAddress: { ...prev.senderAddress, line2: e.target.value }
                    }))}
                    className="form-input"
                    placeholder={t('userSettings.shippingConfig.addressLine2Placeholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="addressLine3" className="form-label">
                    {t('userSettings.shippingConfig.addressLine3')}
                  </label>
                  <input
                    type="text"
                    id="addressLine3"
                    value={shippingConfig.senderAddress.line3}
                    onChange={(e) => setShippingConfig(prev => ({
                      ...prev,
                      senderAddress: { ...prev.senderAddress, line3: e.target.value }
                    }))}
                    className="form-input"
                    placeholder={t('userSettings.shippingConfig.addressLine3Placeholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="postalCode" className="form-label">
                    {t('userSettings.shippingConfig.postalCode')}
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    value={shippingConfig.senderAddress.postalCode}
                    onChange={(e) => setShippingConfig(prev => ({
                      ...prev,
                      senderAddress: { ...prev.senderAddress, postalCode: e.target.value }
                    }))}
                    className="form-input"
                    placeholder={t('userSettings.shippingConfig.postalCodePlaceholder')}
                  />
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">{t('userSettings.shippingConfig.sagawaSettings')}</h3>

                <div className="form-group">
                  <label htmlFor="shukaIraiTimeCode" className="form-label">
                    {t('userSettings.shippingConfig.shukaIraiTimeCode')}
                  </label>
                  <select
                    id="shukaIraiTimeCode"
                    value={shippingConfig.sagawa.shukaIraiTimeCode}
                    onChange={(e) => setShippingConfig(prev => ({
                      ...prev,
                      sagawa: { ...prev.sagawa, shukaIraiTimeCode: e.target.value }
                    }))}
                    className="form-input"
                  >
                    <option value="">{t('userSettings.shippingConfig.selectTimeSlot')}</option>
                    <option value="00">00 - {t('userSettings.shippingConfig.timeSlot00')}</option>
                    <option value="09">09 - {t('userSettings.shippingConfig.timeSlot09')}</option>
                    <option value="12">12 - {t('userSettings.shippingConfig.timeSlot12')}</option>
                    <option value="15">15 - {t('userSettings.shippingConfig.timeSlot15')}</option>
                    <option value="18">18 - {t('userSettings.shippingConfig.timeSlot18')}</option>
                  </select>
                  <small className="form-help">{t('userSettings.shippingConfig.shukaIraiTimeCodeHelp')}</small>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  disabled={savingShippingConfig}
                  className="btn btn-primary"
                >
                  {savingShippingConfig ? t('userSettings.shippingConfig.saving') : t('userSettings.shippingConfig.save')}
                </button>
              </div>

              <p className="form-help-text">
                {t('userSettings.shippingConfig.helpText')}
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSettingsPage; 